// How a puzzle's solution is written, read.
//
// A solution arrives as one string of moves, SAN or UCI, comma-separated,
// with two kinds of branching in it:
//
//   [a|b]  the opponent's replies: each is a line the solver has to meet
//   {a|b}  the solver's alternatives: any one of them answers
//
// e.g. "Re8,[Kh7,Qd3|Bf8,Rxf8,[Kg7,Rxf7|Kxf8,Nf5]]". `toUci` rewrites every
// move in it as UCI, keeping the brackets; `expand` turns it into every line
// from the start to a leaf. Which lines are alternatives and which variations
// is not recorded here: it follows from where they part (see lines.ts).

import { Chess } from "chess.js";

/** One place in a line: a move, or several spellings of the same answer. */
export type Slot = string | string[];

const UCI = /^[a-h][1-8][a-h][1-8][qrbnQRBN]?$/;

export const isUciMove = (move: string): boolean => UCI.test(move);

/** A SAN move in UCI, from the position `game` holds, or null. */
function sanToUci(game: Chess, san: string): string | null {
  const before = game.fen();
  try {
    const move = game.move(san);
    if (!move) return null;
    game.undo();
    return move.from + move.to + (move.promotion || "");
  } catch {
    game.load(before);
    return null;
  }
}

function playOn(game: Chess, move: string): void {
  if (isUciMove(move)) game.move({ from: move.slice(0, 2), to: move.slice(2, 4), promotion: "q" });
  else game.move(move);
}

const splitMoves = (text: string): string[] =>
  text
    .split(",")
    .map((m) => m.trim())
    .filter((m) => m);

/** Split on `|` outside any brackets or braces. */
function splitAlternatives(text: string): string[] {
  const out: string[] = [];
  let current = "";
  let brackets = 0;
  let braces = 0;
  for (const char of text) {
    if (char === "[") brackets++;
    else if (char === "]") brackets--;
    else if (char === "{") braces++;
    else if (char === "}") braces--;
    else if (char === "|" && brackets === 0 && braces === 0) {
      out.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current);
  return out;
}

/** The first `[` and the first `{` at depth 0, -1 where there is none. */
function firstOpenings(text: string): { bracket: number; brace: number } {
  let bracket = -1;
  let brace = -1;
  let brackets = 0;
  let braces = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "[") {
      if (brackets === 0 && braces === 0 && bracket === -1) bracket = i;
      brackets++;
    } else if (c === "]") brackets--;
    else if (c === "{") {
      if (braces === 0 && brackets === 0 && brace === -1) brace = i;
      braces++;
    } else if (c === "}") braces--;
  }
  return { bracket, brace };
}

/** Where the bracket opened at `start` closes. */
function closing(text: string, start: number, open: string, close: string): number {
  let depth = 1;
  let end = start + 1;
  while (end < text.length && depth > 0) {
    if (text[end] === open) depth++;
    if (text[end] === close) depth--;
    end++;
  }
  return end - 1;
}

// --- SAN TO UCI ---

/**
 * The solution with every move written as UCI, brackets kept. A move that
 * cannot be read leaves the part it is in as it came, which the lines check
 * then reports (lines.ts, `branchesOf`).
 */
export function toUci(fen: string, moves: string): string {
  if (moves.includes("[") || moves.includes("{")) return convertBranches(new Chess(fen), moves);
  const list = moves.split(",").map((m) => m.trim());
  if (!list.length || isUciMove(list[0] ?? "")) return moves;
  const game = new Chess(fen);
  const out: string[] = [];
  for (const move of list) {
    const uci = sanToUci(game, move);
    if (uci === null) return moves;
    out.push(uci);
    game.move(move);
  }
  return out.join(",");
}

function convertSimple(game: Chess, text: string): string {
  const moves = splitMoves(text);
  if (!moves.length) return "";
  const out: string[] = [];
  const copy = new Chess(game.fen());
  for (const move of moves) {
    if (isUciMove(move)) {
      out.push(move);
      copy.move({ from: move.slice(0, 2), to: move.slice(2, 4), promotion: "q" });
    } else {
      const uci = sanToUci(copy, move);
      if (uci === null) return text;
      out.push(uci);
      copy.move(move);
    }
  }
  return out.join(",");
}

function playThrough(game: Chess, text: string): void {
  for (const move of splitMoves(text)) playOn(game, move);
}

function convertBranches(game: Chess, text: string): string {
  const { bracket, brace } = firstOpenings(text);
  if (bracket === -1 && brace === -1) return convertSimple(game, text);
  if (brace !== -1 && (bracket === -1 || brace < bracket)) return convertAlternatives(game, text, brace);

  const end = closing(text, bracket, "[", "]");
  const prefix = text.substring(0, bracket);
  const inside = text.substring(bracket + 1, end);
  const suffix = text.substring(end + 1);

  const convertedPrefix = convertSimple(game, prefix);
  playThrough(game, convertedPrefix);
  const afterPrefix = game.fen();

  const branches: string[] = [];
  for (const branch of splitAlternatives(inside)) {
    game.load(afterPrefix);
    branches.push(convertBranches(game, branch));
  }
  game.load(afterPrefix);

  let convertedSuffix = "";
  if (suffix) {
    // A suffix after a set of replies is played from where the first of them
    // ends: they are meant to meet again there.
    if (branches[0] !== undefined) playThrough(game, branches[0]);
    convertedSuffix = convertBranches(game, suffix);
  }
  return (convertedPrefix ? convertedPrefix + "," : "") + "[" + branches.join("|") + "]" + convertedSuffix;
}

/** `{a|b},c`: each alternative is converted with what follows it folded in,
 *  since alternatives reach different positions. */
function convertAlternatives(game: Chess, text: string, start: number): string {
  const end = closing(text, start, "{", "}");
  const prefix = text.substring(0, start).replace(/,\s*$/, "");
  const inside = text.substring(start + 1, end);
  const suffix = text.substring(end + 1).replace(/^\s*,/, "");

  const convertedPrefix = prefix ? convertSimple(game, prefix) : "";
  if (convertedPrefix) playThrough(game, convertedPrefix);
  const before = game.fen();

  const converted: string[] = [];
  for (const alternative of splitAlternatives(inside).map((a) => a.trim()).filter((a) => a)) {
    game.load(before);
    converted.push(convertBranches(game, suffix ? alternative + "," + suffix : alternative));
  }
  game.load(before);
  return (convertedPrefix ? convertedPrefix + "," : "") + "{" + converted.join("|") + "}";
}

// --- EXPANDING INTO LINES ---

/** Every line of the solution, from the start to a leaf. */
export function expand(moves: string): Slot[][] {
  if (!moves.includes("[") && !moves.includes("{")) return [splitMoves(moves)];
  return expandBranches(moves);
}

function expandBranches(text: string): Slot[][] {
  const { bracket, brace } = firstOpenings(text);
  if (bracket === -1 && brace === -1) return [splitMoves(text)];
  if (brace !== -1 && (bracket === -1 || brace < bracket)) return expandAlternatives(text, brace);

  const end = closing(text, bracket, "[", "]");
  const prefix = slotsOf(text.substring(0, bracket));
  const suffix = text.substring(end + 1);
  const out: Slot[][] = [];
  for (const branch of splitAlternatives(text.substring(bracket + 1, end))) {
    for (const path of expandBranches(branch + suffix)) out.push([...prefix, ...path]);
  }
  return out;
}

function expandAlternatives(text: string, start: number): Slot[][] {
  const end = closing(text, start, "{", "}");
  const prefix = slotsOf(text.substring(0, start));
  const suffix = text.substring(end + 1);
  const out: Slot[][] = [];
  for (const alternative of splitAlternatives(text.substring(start + 1, end)).map((a) => a.trim()).filter((a) => a)) {
    const joined = suffix.trim() ? alternative + (suffix.startsWith(",") ? suffix : "," + suffix) : alternative;
    for (const path of expandBranches(joined)) out.push([...prefix, ...path]);
  }
  return out;
}

/** Moves, any of which may be `{a|b}` standing for one slot. */
function slotsOf(text: string): Slot[] {
  if (!text.includes("{")) return splitMoves(text);
  const out: Slot[] = [];
  let current = "";
  let braces = 0;
  const push = () => {
    const item = current.trim();
    if (item) out.push(slotOf(item));
    current = "";
  };
  for (const char of text) {
    if (char === "{") braces++;
    else if (char === "}") braces--;
    if (char === "," && braces === 0) push();
    else current += char;
  }
  push();
  return out;
}

function slotOf(item: string): Slot {
  if (!(item.startsWith("{") && item.endsWith("}"))) return item;
  const alternatives = item
    .slice(1, -1)
    .split("|")
    .map((a) => a.trim())
    .filter((a) => a);
  return alternatives.length === 1 ? (alternatives[0] as string) : alternatives;
}
